import React, { useContext, useState, useEffect, useCallback } from 'react';
import { CrossCircle } from 'Components/DesignSystem/Iconography/Icons';
import { MappingContext } from '../Contexts/MappingContext';
import Select from 'react-select';
import debounce from 'lodash/debounce';

type AttributeContentProps = {
    category: MappingCategory;
    pair: AttributeValuePair;
    pairIndex: number;
    index: number;
};

export const AttributeContent = ({
    category,
    pair,
    pairIndex,
    index
}: AttributeContentProps) => {
    const count = category.attribute.length;
    const {
        updateAttributePair,
        removeAttributePair,
        isEditMode,
        getCategoryDropdownAttributes,
    } = useContext(MappingContext);

    const [allAttributeOptions, setAllAttributeOptions] = useState<{ value: string; label: string }[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);

    // Load attributes based on search and page
    useEffect(() => {
        if (!isEditMode) return;

        const loadAttributes = async () => {
            const result = await getCategoryDropdownAttributes(page, searchValue);

            // Merge with existing selected value if it exists and not in the list (only on page 1)
            let options = result.items;
            if (page === 1 && pair.attribute && !options.find(opt => opt.value === pair.attribute)) {
                options = [{ value: pair.attribute, label: pair.attribute }, ...options];
            }

            // For page 1, replace options; for subsequent pages, append
            setAllAttributeOptions(prevOptions => {
                if (page === 1) {
                    return options;
                } else {
                    // Append new options, avoiding duplicates
                    const existingValues = new Set(prevOptions.map(opt => opt.value));
                    const newOptions = options.filter(opt => !existingValues.has(opt.value));
                    return [...prevOptions, ...newOptions];
                }
            });
            setHasMore(result.hasMore);
        };

        loadAttributes();
    }, [isEditMode, getCategoryDropdownAttributes, pair.attribute, page, searchValue]);

    // Debounced search handler
    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearchValue(value);
            setPage(1);
        }, 500),
        []
    );

    // Handle input change
    const handleInputChange = (input: string) => {
        if (input === '') {
            setSearchValue('');
            setPage(1);
        } else {
            debouncedSearch(input);
        }
    };

    // Handle infinite scroll
    const handleMenuScrollToBottom = async () => {
        if (!hasMore || isLoadingMore) return;

        setIsLoadingMore(true);
        try {
            setPage(prevPage => prevPage + 1);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const handleRemoveAttributePair = () => {
        removeAttributePair(category.id, pairIndex);
    };

    return (
        <div className="tw-flex tw-items-center tw-gap-2 tw-relative">
            {pairIndex > 0 && (
                <div className="tw-absolute tw-w-[2px] tw-bg-edge-strong tw--left-4 tw--top-3.5 tw-h-[calc(50%+14px)]" />
            )}

            {/* AND text between connectors */}
            {pairIndex > 0 && (
                <div
                    className={`tw-absolute tw-text-center tw-justify-start tw-text-ink-tertiary tw-text-xs tw-uppercase tw-leading-none ${index % 2 === 0 ? 'tw-bg-surface' : 'tw-bg-surface-sunken'
                        }`}
                    style={{
                        top: '-14px',
                        left: '-28px',
                        width: '26px',
                        height: '14px'
                    }}
                >
                    And
                </div>
            )}

            {/* Horizontal branch for each item */}
            {count > 1 && (
                <div className="tw-absolute tw-h-[2px] tw-bg-edge-strong tw--left-4 tw-top-1/2 tw--translate-y-1/2 tw-w-2 tw-rounded-r-full" />
            )}
            {count > 1 && pairIndex === 0 && (
                <div className="tw-absolute tw-h-[2px] tw-bg-edge-strong tw--left-[22px] tw-top-1/2 tw--translate-y-1/2 tw-w-2 tw-rounded-l-full" />
            )}

            {/* Vertical line extending down to next item */}
            {pairIndex < category.attribute.length - 1 && (
                <div className="tw-absolute tw-w-[2px] tw-bg-edge-strong tw--left-4 tw-top-1/2 tw-h-[calc(50%+14px)]" />
            )}

            {/* Attribute Dropdown */}
            <div className="tw-flex-1 tw-min-w-0">
                {isEditMode ? (
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Select
                            className="tw-flex-1"
                            options={allAttributeOptions}
                            value={allAttributeOptions.find(opt => opt.value === pair.attribute) || null}
                            onChange={(selectedOption) => {
                                const newAttribute = selectedOption?.value || '';
                                // Clear both attribute and attributeValue when user clears selection
                                updateAttributePair(category.id, pairIndex, {
                                    attribute: newAttribute,
                                    attributeValue: ''
                                });
                            }}
                            placeholder="Select"
                            isClearable={true}
                            maxMenuHeight={220}
                            menuPlacement="auto"
                            // Portal the menu to <body> so it's not clipped by any
                            // overflow:hidden/scroll ancestor (the split-table
                            // body wrapper, the td, etc.). zIndex on menuPortal
                            // keeps it above modals/headers.
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                            onMenuScrollToBottom={handleMenuScrollToBottom}
                            onInputChange={handleInputChange}
                            isLoading={isLoadingMore}
                            components={{ IndicatorSeparator: () => null }}
                            styles={{
                                control: (provided: any) => ({
                                    ...provided,
                                    minHeight: '32px',
                                    fontSize: '0.875rem',
                                    backgroundColor: 'var(--ds-surface)',
                                    borderColor: 'var(--ds-edge-strong)',
                                    '&:hover': { borderColor: 'var(--ds-ink-tertiary)' }
                                }),
                                option: (provided: any, state: any) => ({
                                    ...provided,
                                    fontSize: '0.875rem',
                                    textAlign: 'left',
                                    backgroundColor: state.isFocused ? 'var(--ds-accent-tint)' : 'var(--ds-surface)',
                                    color: 'var(--ds-ink)'
                                }),
                                menu: (provided: any) => ({ ...provided, zIndex: 9999, backgroundColor: 'var(--ds-surface)' }),
                                singleValue: (provided: any) => ({ ...provided, color: 'var(--ds-ink)' }),
                                input: (provided: any) => ({ ...provided, color: 'var(--ds-ink)' }),
                                menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
                                indicatorsContainer: (provided: any) => ({ ...provided, height: '32px' }),
                                valueContainer: (provided: any) => ({ ...provided, padding: '0 8px' })
                            }}
                            data-testid={`attribute-select-${category.id}-${pairIndex}`}
                        />

                        {category.attribute.length > 1 && (
                            <button
                                onClick={handleRemoveAttributePair}
                                aria-label="Remove attribute pair"
                                data-testid={`remove-pair-${category.id}-${pairIndex}`}
                                type="button"
                                className="tw-flex-shrink-0 tw-text-tone-danger-ink hover:tw-text-tone-danger-bold"
                            >
                                <CrossCircle size={18} />
                            </button>
                        )}
                    </div>
                ) : (
                    <span className="tw-text-sm tw-text-ink tw-truncate tw-block tw-max-w-full" title={pair.attribute || ''} data-testid={`attribute-text-${category.id}-${pairIndex}`}>
                        {pair.attribute || '--'}
                    </span>
                )}
            </div>
        </div>
    );
};